import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import {
  formatMoney,
  imageToDataUrl,
  safeText,
  toNumber,
} from "./documentPrintHelpers";
import { shouldShowDiscountColumn } from "../../../utils/discountColumn";
import "./DocumentPrint.css";

const DataLine = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="document-print-data-line">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
};

const RichText = ({ value, fallback = "-" }) => (
  <div
    className="quotation-print-rich-text"
    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(safeText(value, fallback))) }}
  />
);

const hasQuotationDiscount = (document = {}) =>
  shouldShowDiscountColumn(document, document.items || []);

const BankDetails = ({ bank = {} }) => {
  const details = [
    ["Account Holder", bank.accountName],
    ["Bank Name", bank.bankName],
    ["Branch", bank.branchName],
    ["Account Number", bank.accountNumber],
    ["IFSC Code", bank.ifsc],
    ["UPI ID", bank.upiId],
    ["Currency", bank.currency],
    ["Account Type", bank.accountType],
  ].filter(([, value]) => value !== null && value !== undefined && value !== "");

  if (!details.length) return null;

  return (
    <section className="document-print-bank-details">
      <h4>Bank Details</h4>
      <table>
        <tbody>
          {details.map(([label, value]) => (
            <tr key={label}>
              <th>{label}</th>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

const DocumentLikeQuotationPrintTemplate = ({
  document,
  resolvedLogo,
  resolvedStamp,
  resolvedSignature,
  printRef,
  onLogoError,
  onStampError,
  onSignatureError,
}) => {
  const hasDiscount = hasQuotationDiscount(document);
  const showHsn = Boolean(document.showHsn);
  const showTaxColumn = Boolean(document.isGstApplicable);
  const isQuotation = document.type === "quotation";

  return (
  <article ref={printRef} className="document-print-area quotation-print-area">
    <header className="quotation-print-header">
      <div className="quotation-print-company">
        <div className="quotation-print-logo">
          {resolvedLogo ? (
            <img src={resolvedLogo} alt={document.company?.name || "Company"} onError={onLogoError} />
          ) : (
            <div className="document-print-logo-fallback">{String(document.company?.name || "Z").charAt(0)}</div>
          )}
        </div>
        <div className="quotation-print-company-details">
          <h1>{safeText(document.company?.name, "Company")}</h1>
          {document.company?.address && <p>{document.company.address}</p>}
          {document.company?.phone && <p>{document.company.phone}</p>}
          {document.company?.email && <p>{document.company.email}</p>}
          {showTaxColumn && document.company?.gstin && <p>GSTIN: {document.company.gstin}</p>}
        </div>
      </div>
      <h2>{safeText(document.title, "QUOTATION")}</h2>
    </header>

    <section className="quotation-print-meta">
      <div>
        <DataLine label={document.labels?.number || "#"} value={document.meta?.number} />
        <DataLine label={document.labels?.date || "Date"} value={document.meta?.date} />
        <DataLine label={document.labels?.due || "Expiry Date"} value={document.meta?.due} />
        {isQuotation && <DataLine label="Expiry Date" value={document.meta?.expiryDate} />}
      </div>
      <div />
    </section>

    <section className="quotation-print-bill-to">
      <span>{document.labels?.party || "Bill To"}</span>
      <h3>{safeText(document.party?.name, "Party")}</h3>
      {(document.party?.address || document.party?.phone || document.party?.email) && (
        <p>{[document.party?.address, document.party?.phone, document.party?.email].filter(Boolean).join("\n")}</p>
      )}
      {showTaxColumn && document.party?.gstin && <p>GSTIN: {document.party.gstin}</p>}
    </section>

    {document.subject && (
      <section className="quotation-print-subject">
        <span>Subject :</span>
        <RichText value={document.subject} />
      </section>
    )}

    <table className="document-print-table quotation-print-table">
      <colgroup>
        <col className="document-print-col-serial" />
        <col className="document-print-col-product" />
        {showHsn && <col className="document-print-col-hsn" />}
        <col className="document-print-col-qty" />
        <col className="document-print-col-rate" />
        {hasDiscount && <col className="document-print-col-discount" />}
        {showTaxColumn && <col className="document-print-col-tax" />}
        <col className="document-print-col-amount" />
      </colgroup>
      <thead>
        <tr>
          <th>#</th>
          <th>Item &amp; Description</th>
          {showHsn && <th>HSN/SAC</th>}
          <th>Qty</th>
          <th>Rate</th>
          {hasDiscount && <th>Discount</th>}
          {showTaxColumn && <th>Tax</th>}
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        {(document.items || []).map((item, index) => (
          <tr key={item.id || index}>
            <td>{item.serial || index + 1}</td>
            <td>{safeText(item.name, "")}</td>
            {showHsn && <td>{safeText(item.hsn, "")}</td>}
            <td>{safeText(item.quantity, "")}</td>
            <td>{formatMoney(item.rate)}</td>
            {hasDiscount && <td>{safeText(item.discount, "-")} {item.discountType || ""}</td>}
            {showTaxColumn && <td>{item.taxRate ? `${item.taxRate}%` : ""}</td>}
            <td>{formatMoney(item.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>

    <section className="quotation-print-summary">
      <div className="quotation-print-amount-words">
        <h4>Total In Words</h4>
        <strong>{safeText(document.totals?.amountInWords, "Zero Rupees Only")}</strong>
      </div>
      <div className="quotation-print-totals">
        <DataLine label="Sub Total" value={formatMoney(document.totals?.subtotal)} />
        {toNumber(document.totals?.discount) !== 0 && <DataLine label="Discount" value={formatMoney(document.totals?.discount)} />}
        {document.isGstApplicable && <DataLine label="Taxable Amount" value={formatMoney(document.totals?.taxableAmount)} />}
        {document.isGstApplicable && Array.isArray(document.totals?.gstRows) && document.totals.gstRows.length > 0 ? (
          document.totals.gstRows.map((row) => (
            <DataLine key={row.label} label={row.label} value={formatMoney(row.amount)} />
          ))
        ) : (
          <>
            {document.isGstApplicable && toNumber(document.totals?.cgst) !== 0 && <DataLine label="CGST" value={formatMoney(document.totals?.cgst)} />}
            {document.isGstApplicable && toNumber(document.totals?.sgst) !== 0 && <DataLine label="SGST" value={formatMoney(document.totals?.sgst)} />}
            {document.isGstApplicable && toNumber(document.totals?.igst) !== 0 && <DataLine label="IGST" value={formatMoney(document.totals?.igst)} />}
          </>
        )}
        {toNumber(document.totals?.adjustment) !== 0 && (
          <DataLine label={document.totals?.adjustmentLabel || "Round Off"} value={formatMoney(document.totals?.adjustment)} />
        )}
        <div className="quotation-print-total"><span>Total</span><strong>{formatMoney(document.totals?.grandTotal)}</strong></div>
        {toNumber(document.totals?.paidAmount) !== 0 && <DataLine label="Paid Amount" value={formatMoney(document.totals?.paidAmount)} />}
        {toNumber(document.totals?.balanceDue) !== 0 && <DataLine label="Balance Due" value={formatMoney(document.totals?.balanceDue)} />}
        <div className="quotation-print-signature">
          {(resolvedStamp || resolvedSignature) && <div className="document-print-stamp-signature">
            {resolvedStamp && <img className="document-print-stamp" src={resolvedStamp} alt="" onError={onStampError} />}
            {resolvedSignature && <img className="document-print-sign-image" src={resolvedSignature} alt="" onError={onSignatureError} />}
          </div>}
          <span>Authorized Signature</span>
        </div>
      </div>
    </section>

    <section className="quotation-print-notes">
      {document.details && (
        <div className="quotation-print-details">
          <RichText value={document.details} />
        </div>
      )}
      <h4 className="quotation-print-terms-heading">Terms &amp; Conditions</h4>
      <RichText value={document.termsAndCondition} />
      {document.notes && <><h4>Note</h4><p>{document.notes}</p></>}
      <BankDetails bank={document.bank} />
    </section>
  </article>
  );
};

const DocumentPrintTemplate = React.forwardRef(({ document }, ref) => {
  const [logoSrc, setLogoSrc] = useState("");
  const [logoFailed, setLogoFailed] = useState(false);
  const [stampSrc, setStampSrc] = useState("");
  const [stampFailed, setStampFailed] = useState(false);
  const [signatureSrc, setSignatureSrc] = useState("");
  const [signatureFailed, setSignatureFailed] = useState(false);
  const logoUrl = document?.company?.logoUrl || "";
  const stampUrl = document?.company?.stampUrl || "";
  const signatureUrl = document?.company?.signatureUrl || "";

  useEffect(() => {
    let mounted = true;
    setLogoFailed(false);
    setStampFailed(false);
    setSignatureFailed(false);
    imageToDataUrl(logoUrl, "logo").then((src) => mounted && setLogoSrc(src || ""));
    imageToDataUrl(stampUrl, "stamp").then((src) => mounted && setStampSrc(src || ""));
    imageToDataUrl(signatureUrl, "signature").then((src) => mounted && setSignatureSrc(src || ""));
    return () => {
      mounted = false;
    };
  }, [logoUrl, stampUrl, signatureUrl]);

  if (!document) return null;

  const isGst = Boolean(document.isGstApplicable);
  const showHsn = Boolean(document.showHsn);
  const showDiscount = Boolean(document.showDiscount);
  const hasCgst = isGst && toNumber(document.totals?.cgst) !== 0;
  const hasSgst = isGst && toNumber(document.totals?.sgst) !== 0;
  const hasIgst = isGst && toNumber(document.totals?.igst) !== 0;
  const showTaxColumn = isGst;
  const resolvedLogo = logoFailed ? "" : logoSrc || logoUrl;
  const resolvedStamp = stampFailed ? "" : stampSrc || stampUrl;
  const resolvedSignature = signatureFailed ? "" : signatureSrc || signatureUrl;

  if (
    document.documentType === "quotation" ||
    document.documentType === "invoice" ||
    document.documentType === "pro_forma" ||
    document.documentType === "proforma"
  ) {
    return (
      <DocumentLikeQuotationPrintTemplate
        document={document}
        resolvedLogo={resolvedLogo}
        resolvedStamp={resolvedStamp}
        resolvedSignature={resolvedSignature}
        printRef={ref}
        onLogoError={() => setLogoFailed(true)}
        onStampError={() => setStampFailed(true)}
        onSignatureError={() => setSignatureFailed(true)}
      />
    );
  }

  return (
    <article ref={ref} className="document-print-area">
      <header className="document-print-header document-print-bordered">
        <div className="document-print-brand">
          {resolvedLogo ? (
            <img
              src={resolvedLogo}
              alt={document.company?.name || "Company"}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div className="document-print-logo-fallback">{String(document.company?.name || "Z").charAt(0)}</div>
          )}
          <div>
            <h1>{safeText(document.company?.name, "Company")}</h1>
            <p>{safeText(document.company?.address, "")}</p>
            <DataLine label="Phone" value={document.company?.phone} />
            <DataLine label="Email" value={document.company?.email} />
            {isGst && <DataLine label="GSTIN" value={document.company?.gstin} />}
          </div>
        </div>
        <div className="document-print-title-block">
          <h2>{safeText(document.title, "DOCUMENT")}</h2>
        </div>
      </header>

      <section className="document-print-info-grid document-print-bordered">
        <div className="document-print-party">
          <span className="document-print-eyebrow">{document.labels?.party || "Bill To"}</span>
          <h3>{safeText(document.party?.name, "Party")}</h3>
          <p>{safeText(document.party?.address, "")}</p>
          <DataLine label="State" value={document.party?.state} />
          {isGst && <DataLine label="GSTIN" value={document.party?.gstin} />}
          <DataLine label="Phone" value={document.party?.phone} />
          <DataLine label="Email" value={document.party?.email} />
        </div>

        <div className="document-print-meta-table">
          <DataLine label={document.labels?.number || "Document No"} value={document.meta?.number} />
          <DataLine label={document.labels?.date || "Document Date"} value={document.meta?.date} />
          <DataLine label={document.labels?.due || "Due Date"} value={document.meta?.due} />
          {document.type !== "quotation" && <DataLine label="Terms" value={document.meta?.terms} />}
          <DataLine label="Reference" value={document.meta?.reference} />
          <DataLine label="Category" value={document.meta?.category} />
        </div>
      </section>

      <table className="document-print-table">
        <colgroup>
          <col className="document-print-col-serial" />
          <col className="document-print-col-product" />
          {showHsn && <col className="document-print-col-hsn" />}
          <col className="document-print-col-qty" />
          <col className="document-print-col-rate" />
          {showDiscount && <col className="document-print-col-discount" />}
          {showTaxColumn && <col className="document-print-col-tax" />}
          <col className="document-print-col-amount" />
        </colgroup>
        <thead>
          <tr>
            <th>Sr No</th>
            <th>Item & Description</th>
            {showHsn && <th>HSN/SAC</th>}
            <th>Qty</th>
            <th>Rate</th>
            {showDiscount && <th>Discount</th>}
            {showTaxColumn && <th>Tax</th>}
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {(document.items || []).map((item, index) => (
            <tr key={item.id || index}>
              <td>{item.serial || index + 1}</td>
              <td>{safeText(item.name, "")}</td>
              {showHsn && <td>{safeText(item.hsn, "")}</td>}
              <td>{safeText(item.quantity, "")}</td>
              <td>{formatMoney(item.rate)}</td>
              {showDiscount && (
                <td>
                  {safeText(item.discount, "")}
                  {item.discount && item.discountType ? ` (${item.discountType})` : ""}
                </td>
              )}
              {showTaxColumn && <td>{item.taxRate ? `${item.taxRate}%` : ""}</td>}
              <td>{formatMoney(item.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <section className="document-print-bottom-grid">
        <div className="document-print-notes">
          <h4>Total In Word</h4>
          <p>{safeText(document.totals?.amountInWords, "Zero Rupees Only")}</p>

          {document.notes && (
            <>
              <h4>Note</h4>
              <p>{document.notes}</p>
            </>
          )}

          {document.termsAndCondition && (
            <>
              <h4>Terms & Condition</h4>
              <RichText value={document.termsAndCondition} />
            </>
          )}

          <BankDetails bank={document.bank} />
        </div>

        <div className="document-print-total-card">
          <DataLine label="Sub Total" value={formatMoney(document.totals?.subtotal)} />
          {document.totals?.discount !== "" && toNumber(document.totals?.discount) !== 0 && (
            <DataLine label="Discount" value={formatMoney(document.totals?.discount)} />
          )}
          {isGst && <DataLine label="Taxable Amount" value={formatMoney(document.totals?.taxableAmount)} />}
          {isGst && Array.isArray(document.totals?.gstRows) && document.totals.gstRows.length > 0 ? (
            document.totals.gstRows.map((row) => (
              <DataLine key={row.label} label={row.label} value={formatMoney(row.amount)} />
            ))
          ) : (
            <>
              {hasCgst && <DataLine label="CGST" value={formatMoney(document.totals?.cgst)} />}
              {hasSgst && <DataLine label="SGST" value={formatMoney(document.totals?.sgst)} />}
              {hasIgst && <DataLine label="IGST" value={formatMoney(document.totals?.igst)} />}
            </>
          )}
          {document.totals?.adjustment !== "" && toNumber(document.totals?.adjustment) !== 0 && (
            <DataLine label={document.totals?.adjustmentLabel || "Round Off"} value={formatMoney(document.totals?.adjustment)} />
          )}
          <div className="document-print-grand-total">
            <span>Total</span>
            <strong>{formatMoney(document.totals?.grandTotal)}</strong>
          </div>
          {document.totals?.paidAmount !== "" && toNumber(document.totals?.paidAmount) !== 0 && (
            <DataLine label="Paid Amount" value={formatMoney(document.totals?.paidAmount)} />
          )}
          {document.totals?.balanceDue !== "" && toNumber(document.totals?.balanceDue) !== 0 && (
            <DataLine label="Balance Due" value={formatMoney(document.totals?.balanceDue)} />
          )}
        </div>

        <div className="document-print-signature">
          {(resolvedStamp || resolvedSignature) && (
            <div className="document-print-stamp-signature">
              {resolvedStamp && (
                <img
                  className="document-print-stamp"
                  src={resolvedStamp}
                  alt=""
                  onError={() => setStampFailed(true)}
                />
              )}
              {resolvedSignature && (
                <img
                  className="document-print-sign-image"
                  src={resolvedSignature}
                  alt=""
                  onError={() => setSignatureFailed(true)}
                />
              )}
            </div>
          )}
          <div>
            <p>For, {safeText(document.company?.name, "Company")}</p>
            <span>(Authorised Signatory)</span>
          </div>
        </div>
      </section>
    </article>
  );
});

DocumentPrintTemplate.displayName = "DocumentPrintTemplate";

export default DocumentPrintTemplate;
